import { useEffect, useReducer, useRef, useState } from 'react';

import { useDebouncedValue } from './useDebouncedValue';

/** How long typing must pause before the server is asked. */
export const REMOTE_SEARCH_DELAY_MS = 250;

/** The fewest characters worth sending; one letter matches most of a book. */
export const REMOTE_SEARCH_MIN_LENGTH = 2;

export interface RemoteSearchOptions {
  /** How long typing must pause before the search runs. */
  readonly delayMs?: number;
  /** The fewest characters worth searching for. */
  readonly minLength?: number;
  /** Set false to hold the search, for example while the field is closed. */
  readonly enabled?: boolean;
  /**
   * What the search is over. A change throws away what was found and searches again, as when the
   * same words now mean something else.
   */
  readonly scope?: string;
}

export interface RemoteSearch<R> {
  /**
   * Where the search is: `idle` before there is enough to search for, `searching` while the answer
   * for the latest words is on its way, then `ready` or `error`.
   */
  readonly status: 'idle' | 'searching' | 'ready' | 'error';
  /**
   * The latest answer. It is kept while the next one is on its way, so the list does not blink
   * between keystrokes, but only while it still bears on what is typed.
   */
  readonly data: R | undefined;
  /** Whether `data` answers the words typed now rather than an earlier version of them. */
  readonly current: boolean;
  readonly error: Error | undefined;
  /** Asks again for the words typed now. */
  readonly retry: () => void;
}

interface Outcome<R> {
  readonly term: string;
  readonly scope: string;
  readonly attempt: number;
  readonly data?: R;
  readonly error?: Error;
}

function asError(thrown: unknown): Error {
  return thrown instanceof Error ? thrown : new Error('The search failed.');
}

/** Whether an earlier answer is about the words now typed: one is a start of the other. */
function bearsOn(answered: string, typed: string): boolean {
  const a = answered.toLowerCase();
  const b = typed.toLowerCase();
  return a.startsWith(b) || b.startsWith(a);
}

/**
 * Searches a server as the person types.
 *
 * The words are waited on until typing pauses, the search runs once for them, and a search that is
 * overtaken is cancelled through its signal, so an old answer can neither arrive late nor spend the
 * server's time. An answer for an earlier version of the words stays on screen until the next one
 * lands, which is what stops a list blinking between keystrokes.
 *
 * Reading the search through a ref means a caller may pass an inline function without it starting
 * the search over on every render.
 *
 * @param typed the words typed, trimmed
 * @param search asks the server; it should pass the signal on to whatever it calls
 * @param options see {@link RemoteSearchOptions}
 * @returns where the search is, and the latest answer
 */
export function useRemoteSearch<R>(
  typed: string,
  search: (term: string, signal: AbortSignal) => Promise<R>,
  {
    delayMs = REMOTE_SEARCH_DELAY_MS,
    minLength = REMOTE_SEARCH_MIN_LENGTH,
    enabled = true,
    scope = '',
  }: RemoteSearchOptions = {},
): RemoteSearch<R> {
  const settled = useDebouncedValue(typed, delayMs);
  const [attempt, retry] = useReducer((count: number) => count + 1, 0);
  const [outcome, setOutcome] = useState<Outcome<R> | null>(null);
  const latest = useRef(search);
  useEffect(() => {
    latest.current = search;
  });

  const active = enabled && settled.length >= minLength;
  useEffect(() => {
    if (!active) {
      return undefined;
    }
    const controller = new AbortController();
    latest.current(settled, controller.signal).then(
      (data) => {
        if (!controller.signal.aborted) {
          setOutcome({ term: settled, scope, attempt, data });
        }
      },
      (thrown: unknown) => {
        if (!controller.signal.aborted) {
          setOutcome({ term: settled, scope, attempt, error: asError(thrown) });
        }
      },
    );
    return () => {
      controller.abort();
    };
  }, [active, settled, scope, attempt]);

  if (!enabled || typed.length < minLength) {
    return { status: 'idle', data: undefined, current: false, error: undefined, retry };
  }
  if (outcome?.scope !== scope) {
    return { status: 'searching', data: undefined, current: false, error: undefined, retry };
  }
  const data =
    outcome.error === undefined && bearsOn(outcome.term, typed) ? outcome.data : undefined;
  const answers = outcome.term === settled && outcome.term === typed && outcome.attempt === attempt;
  if (!answers) {
    return { status: 'searching', data, current: false, error: undefined, retry };
  }
  return {
    status: outcome.error === undefined ? 'ready' : 'error',
    data,
    current: true,
    error: outcome.error,
    retry,
  };
}
